import {
  ATLAS_AI_NOTE_THINKING,
  ATLAS_AI_NOTE_TURN_TEXT,
  ATLAS_AI_NOTE_WORKING,
} from '@common/constants/webSockets'
import { IAtlasAITurnText } from '@common/interfaces/atlasai'
import { INotesCount, INotesData, INote, NotesTags } from '@common/interfaces/notes'
import orderBy from 'lodash/orderBy'
import { combineReducers } from 'redux'
import { loadingArrayReducer } from '../../helpers/helpers'
import { getCellNotesListKey, ICellNotesListScope } from '../../helpers/notesPanel'
import { IAction, IActionWithParams, IPaginationParams } from '../apiMiddleware'
import * as cellNotesActions from '../cellNotes/actions'
import { UPDATE_TAG_ORDER_SUCCESS } from '../dueDiligence/actions'
import * as actions from './actions'
import { notesComposerRequested, notesPanelForcedTab, notesPanelState } from './panelReducers'
import { insertReply, removeFromTree, updateInTree } from './threadHelpers'

/** What ConfirmLink reports about the note a suggestion's words came from. */
interface ISourceNoteOutcome {
  id: string
  deleted: boolean
  noteText: string | null
  note: unknown
}

interface TagOrderItem {
  tag: string
  sortOrder: number
}

/**
 * Runs one tree operation over every list of notes this slice holds.
 *
 * Threads live in `data` and, on the diligence page, again inside each
 * `dataByTags` bucket — the sectioned/fullscreen view renders from those. Only
 * `data` used to be updated on a reply action, so the two views disagreed about
 * a thread until the next refetch. Applying the operation to both keeps them in
 * step, and the helpers return the original arrays when nothing matched, so
 * buckets that don't hold the thread keep their identity.
 */
const applyToThreads = (state: INotesData, apply: (notes: INote[]) => INote[]): INotesData => ({
  ...state,
  data: apply(state.data),
  dataByTags: state.dataByTags
    ? (Object.fromEntries(
        Object.entries(state.dataByTags).map(([tag, notes]) => [tag, apply(notes)]),
      ) as Record<NotesTags, INote[]>)
    : state.dataByTags,
})

const notesReducer = combineReducers({
  // GET_ARC_PROGRESS_REQUEST is a background rehydrate fetch (fires whenever a
  // visible note is Arc-working); it must not flip the notes-panel global
  // loader, or the list flashes its skeleton mid-run.
  loadingArray: loadingArrayReducer([actions.prefix], [actions.GET_ARC_PROGRESS_REQUEST]),

  // Panel presentation state (state / width / force latch / composer request) lives
  // in `panelReducers.ts` — see the note there on why it is split out and on why
  // `hideNotes` is deliberately not part of it.
  notesPanelState,
  notesPanelForcedTab,
  notesComposerRequested,
  // Rail counts. Kept separate from `notes` so a collapsed page pays for one
  // aggregate instead of a full list fetch.
  count: (state: INotesCount | null = null, action: IAction): INotesCount | null => {
    switch (action.type) {
      case actions.COUNT_NOTES_SUCCESS:
        // A bodiless reply (CloudFront serves API 404s as an HTML 200) would otherwise return
        // undefined, which makes Redux throw and strands COUNT_NOTES_REQUEST in loadingArray.
        return (action.data as INotesCount | undefined) ?? null
      case actions.MARK_NOTES_READ_SUCCESS: {
        // Decrement by what the server actually inserted, not by how many ids we
        // sent — re-sending an already-read note must not drive the badge negative.
        if (!state) return state
        const { markedRead = 0 } = (action.data || {}) as { markedRead?: number }
        return { ...state, unread: Math.max(0, state.unread - markedRead) }
      }
      default:
        return state
    }
  },
  notes: (
    state: INotesData | null = null,
    action: IActionWithParams<IPaginationParams>,
  ): INotesData | null => {
    switch (action.type) {
      case actions.MARK_NOTES_READ_REQUEST: {
        // Optimistic: clear the unread treatment as soon as we report the read, so
        // the list doesn't keep highlighting a note the user is looking at.
        if (!state) return null
        const readIds = new Set((action as unknown as { noteIds?: string[] }).noteIds || [])
        if (readIds.size === 0) return state
        return {
          ...state,
          data: state.data.map((note) =>
            readIds.has(note.id) && note.isUnread ? { ...note, isUnread: false } : note,
          ),
        }
      }
      case actions.LIST_NOTES_SUCCESS: {
        const data = action.data as INotesData
        const { id: listId } = action as { id?: string }
        const cellListKey = getCellNotesListKey((action.params ?? {}) as ICellNotesListScope)
        if (!action.params?.loadMore) {
          return { ...data, listId, cellListKey }
        }
        // A page fetched for another scope of the same record (the full list while a cell
        // opened, or another cell) must not be appended to the list on screen.
        if (!state || state.listId !== listId || state.cellListKey !== cellListKey) {
          return state
        }
        return {
          ...state,
          data: [...state.data, ...data.data],
        }
      }
      case UPDATE_TAG_ORDER_SUCCESS: {
        const tagOrderData = action.data as { tags?: TagOrderItem[] } | undefined
        if (state && tagOrderData?.tags) {
          const orderedTags = tagOrderData.tags
            .sort((a, b) => a.sortOrder - b.sortOrder)
            .map((item) => item.tag)
          return {
            ...state,
            distinctTags: orderedTags,
          }
        }
        return state
      }
      case actions.ADD_NOTE_SUCCESS: {
        if (!state) return null
        const note = action.data as INote & {
          dragOrderChange?: boolean
          sortDirection?: 'ASC' | 'DESC'
          newsfeedHeadlineId?: string | null
        }
        if (note.newsfeedHeadlineId && note.newsfeedHeadlineId !== state.listId) {
          return state
        }
        if (note.parentId) {
          // Recursive: the parent may be a reply at any depth. Matching only
          // top-level notes dropped every reply-to-a-reply on the floor.
          return applyToThreads(state, (notes) => insertReply(notes, note))
        } else if (note.dragOrderChange) {
          return {
            ...state,
            total: state.total + 1,
            data: note.sortDirection === 'ASC' ? [note, ...state.data.slice(0, -1)] : state.data,
          }
        }
        const sortedExistingNotes = orderBy(
          state.data,
          ['pinned', 'resolved', 'createdAt'],
          ['desc', 'asc', 'desc'],
        )
        return {
          ...state,
          total: state.total + 1,
          data: [note, ...sortedExistingNotes],
        }
      }
      // Approving a suggestion rewrites the note the words came from, or deletes it once
      // nothing attributable is left. The response identifies the link, so without this the
      // drawer kept showing the text that had already moved until the page was reloaded.
      case cellNotesActions.CONFIRM_CELL_NOTE_LINK_SUCCESS: {
        if (!state) return null
        const source = (action.data as { sourceNote?: ISourceNoteOutcome })?.sourceNote
        if (!source) return state

        if (source.deleted) {
          // The total only moves when this list actually held the note. A drawer scoped or
          // filtered to something else never showed it, and decrementing there would leave a
          // count that disagrees with the rows on screen until the next refetch.
          const wasListed = state.data.some((item) => item.id === source.id)
          return {
            ...applyToThreads(state, (notes) => removeFromTree(notes, source.id)),
            total: wasListed ? Math.max(0, state.total - 1) : state.total,
          }
        }

        // Partial payload on purpose: only the text changed, and updateInTree keeps the
        // replies the store already holds when the payload carries none.
        const rewritten = {
          id: source.id,
          noteText: source.noteText,
          note: source.note,
        } as unknown as INote
        return applyToThreads(state, (notes) => updateInTree(notes, rewritten))
      }
      case actions.DELETE_NOTE_SUCCESS: {
        if (!state) return null
        const deletedNote = action.data as INote & { newsfeedHeadlineId?: string | null }
        if (deletedNote?.newsfeedHeadlineId && deletedNote.newsfeedHeadlineId !== state.listId) {
          return state
        }
        if (deletedNote?.parentId) {
          // Recursive, and prunes the whole subtree — the server cascades the
          // delete to nested replies, so keeping them would misreport the thread.
          return applyToThreads(state, (notes) => removeFromTree(notes, deletedNote.id))
        }
        return {
          ...state,
          total: state.total - 1,
          data: state.data.filter((item) => item.id !== deletedNote.id),
        }
      }
      case actions.UPDATE_NOTE_SUCCESS: {
        if (!state) return null
        const note = action.data as INote & {
          dragOrderChange?: boolean
          isCallAgendaSort?: boolean
          sortDirection?: 'ASC' | 'DESC'
          newsfeedHeadlineId?: string | null
        }
        if (note.newsfeedHeadlineId && note.newsfeedHeadlineId !== state.listId) {
          return state
        }
        if (note.parentId) {
          // Recursive: the edited reply can sit at any depth, and its own
          // replies must survive the swap.
          return applyToThreads(state, (notes) => updateInTree(notes, note))
        } else if (note.dragOrderChange) {
          return state
        } else if (note.isCallAgendaSort) {
          const sortDirection = (note.sortDirection?.toLowerCase() as 'asc' | 'desc') || 'asc'
          let itemRemoved = false
          const updatedData = orderBy(
            state.data
              .map((item) => {
                if (item.id === note.id) {
                  if (item.resolved !== note.resolved) {
                    itemRemoved = true
                    return null
                  }
                  return note
                }
                return item
              })
              .filter((item): item is INote => item !== null),
            ['dragOrder'],
            [sortDirection],
          )

          return {
            ...state,
            data: updatedData,
            total: itemRemoved ? state.total - 1 : state.total,
          }
        }
        const updatedData = orderBy(
          state.data.map((item) => {
            if (item.id === note.id) {
              return note
            }
            return item
          }),
          ['pinned', 'resolved', 'createdAt'],
          ['desc', 'asc', 'desc'],
        )

        const updatedDataByTags = state.dataByTags
          ? {
              ...state.dataByTags,
              ...Object.entries(state.dataByTags).reduce<Record<NotesTags, INote[]>>(
                (acc, [tagKey, tagNotes]) => {
                  const tag = tagKey as NotesTags
                  acc[tag] = tagNotes.map((existing: INote) => {
                    if (existing.id === note.id) {
                      return note
                    }
                    return existing
                  })
                  return acc
                },
                {} as Record<NotesTags, INote[]>,
              ),
            }
          : undefined

        return {
          ...state,
          data: updatedData,
          dataByTags: updatedDataByTags,
          total: state.total,
        }
      }
      case actions.HIDE_NOTES:
        return null

      // Arc flipped its working state on a note thread (pushed live over the
      // websocket). Update the matching root note's `isArcWorking` so the
      // "Arc is working…" indicator appears/disappears without a refetch.
      case ATLAS_AI_NOTE_WORKING: {
        if (!state) return state
        const { noteId, isArcWorking } = action.data as {
          noteId: string
          isArcWorking: boolean
        }
        return {
          ...state,
          data: state.data.map((item) => (item.id === noteId ? { ...item, isArcWorking } : item)),
        }
      }

      default:
        return state
    }
  },
  // Records the entity id + params of the most recent (non-loadMore) notes
  // fetch so an Arc-write refetch saga can silently reload the exact same list
  // (component-held filter/pagination params aren't otherwise in redux). Reset
  // when the notes panel is hidden.
  lastListContext: (
    state: { id: string; params: IPaginationParams } | null = null,
    action: IActionWithParams<IPaginationParams> & { id?: string },
  ): { id: string; params: IPaginationParams } | null => {
    switch (action.type) {
      case actions.LIST_NOTES_REQUEST:
        if (action.params?.loadMore || !action.id) {
          return state
        }
        return { id: action.id, params: action.params ?? {} }
      case actions.HIDE_NOTES:
        return null
      default:
        return state
    }
  },
  // Arc's live reasoning per note thread, keyed by root note id. Seeded by the
  // lazy arc-progress fetch (rehydrate on open/refresh) and appended to live via
  // the note-scoped websocket events, so the note's ReasoningPanel mirrors the
  // Atlas chat. Cleared for a thread once Arc stops working it.
  arcProgress: (
    state: Record<string, IArcNoteProgress> = {},
    action: IAction & { id?: string },
  ): Record<string, IArcNoteProgress> => {
    switch (action.type) {
      case actions.GET_ARC_PROGRESS_SUCCESS: {
        if (!action.id) return state
        const data = action.data as IArcNoteProgress
        // Merge with any live steps that arrived before this fetch resolved —
        // live (existing) wins per turnIndex so a websocket step is never
        // clobbered by slightly-staler persisted data.
        const existing = state[action.id]
        const byIndex = new Map<number, IAtlasAITurnText>()
        data.turnTexts.forEach((turn) => byIndex.set(turn.turnIndex, turn))
        existing?.turnTexts.forEach((turn) => byIndex.set(turn.turnIndex, turn))
        const turnTexts = [...byIndex.values()].sort((a, b) => a.turnIndex - b.turnIndex)
        return {
          ...state,
          [action.id]: { thinking: existing?.thinking ?? data.thinking, turnTexts },
        }
      }
      case ATLAS_AI_NOTE_THINKING: {
        const { noteId, thinking } = action.data as { noteId: string; thinking: string }
        const current = state[noteId] ?? { thinking: null, turnTexts: [] }
        return { ...state, [noteId]: { ...current, thinking } }
      }
      case ATLAS_AI_NOTE_TURN_TEXT: {
        const { noteId, ...turn } = action.data as { noteId: string } & IAtlasAITurnText
        const current = state[noteId] ?? { thinking: null, turnTexts: [] }
        // Dedupe by turnIndex — a replayed/duplicated step replaces, never doubles.
        const turnTexts = current.turnTexts.filter((t) => t.turnIndex !== turn.turnIndex)
        turnTexts.push(turn)
        turnTexts.sort((a, b) => a.turnIndex - b.turnIndex)
        return { ...state, [noteId]: { ...current, turnTexts } }
      }
      case ATLAS_AI_NOTE_WORKING: {
        // Arc finished (or archived thread) — drop the trace so the next run
        // starts clean. Keep it while working so the panel persists.
        const { noteId, isArcWorking } = action.data as { noteId: string; isArcWorking: boolean }
        if (isArcWorking || !state[noteId]) return state
        const next = { ...state }
        delete next[noteId]
        return next
      }
      case actions.HIDE_NOTES:
        return {}
      default:
        return state
    }
  },
})

type INotesState = ReturnType<typeof notesReducer>
type INotesAction = Parameters<typeof notesReducer>[1]

/**
 * A full-list response (success or failure) that isn't for the latest list request. The
 * middleware carries the same params object from REQUEST to SUCCESS/FAILURE, so identity matches
 * them: clicking cell A then B must not let A's slower response paint B's drawer.
 */
const isStaleListResponse = (
  state: INotesState | undefined,
  action: IActionWithParams<IPaginationParams> & { id?: string },
): boolean =>
  (action.type === actions.LIST_NOTES_SUCCESS || action.type === actions.LIST_NOTES_FAILURE) &&
  // Mirrors what `lastListContext` records, so a response is only judged against its own kind.
  !!action.id &&
  !!action.params &&
  !action.params.loadMore &&
  !!state?.lastListContext &&
  action.params !== state.lastListContext.params

// The list's loader follows the latest request only. `loadingArray` clears every LIST_NOTES entry
// on the first response, so a dropped one keeps the entry while the latest request is in flight —
// unless that request skipped the loader, since its own response will never clear the entry.
const notes = (state: INotesState | undefined, action: INotesAction): INotesState => {
  const next = notesReducer(state, action)
  const isStale =
    !!state &&
    isStaleListResponse(state, action as IActionWithParams<IPaginationParams> & { id?: string })
  if (!isStale) {
    return next
  }
  const isLatestWithoutLoader = !!state.lastListContext?.params.skipLoader
  return {
    ...next,
    notes: state.notes,
    loadingArray: isLatestWithoutLoader ? next.loadingArray : state.loadingArray,
  }
}

export default notes

interface IArcNoteProgress {
  thinking: string | null
  turnTexts: IAtlasAITurnText[]
}
